'use client';

import React, { useState, useCallback } from 'react';
import Link from 'next/link';

interface FlowNode {
  id: string;
  label: string;
  arabic: string;
  sublabel: string;
  href: string;
  color: string;
  x: number;
  y: number;
  desc: string;
  terms: string[];
}

const FLOW_NODES: FlowNode[] = [
  {
    id: 'keys',
    label: 'KEYS',
    arabic: 'مَفَاتِيح',
    sublabel: 'Keys to Kulli Haal',
    href: '/keys-to-kulli-haal',
    color: '#D4A82A',
    x: 80,
    y: 160,
    desc: 'Seven conceptual frameworks — Ayat, Haal, Taqwah, Mizan, Amanah, Falah, Yaqeen',
    terms: ['Taqwah', 'Amanah', 'Falah', 'Yaqeen', 'Wujud', 'Ihsan', 'Furqan'],
  },
  {
    id: 'ayat',
    label: 'AYAT',
    arabic: 'آيَات',
    sublabel: 'Ayat in Creation',
    href: '/ayat-in-creation',
    color: '#2d7a47',
    x: 240,
    y: 80,
    desc: 'Signs of Allah in creation — ecosystems, communities, patterns',
    terms: ['Zahir', 'Batin', 'Haqq', 'Sunnat Allah'],
  },
  {
    id: 'names',
    label: 'NAMES',
    arabic: 'أَسْمَاء',
    sublabel: 'Names Reference',
    href: '/names-reference',
    color: '#8a8ab8',
    x: 400,
    y: 160,
    desc: '99 Names of Allah — the vocabulary of recognition',
    terms: ['Al-Khaliq', 'Ar-Razzaq', 'Al-Adl', 'An-Nur', 'Al-Ahad'],
  },
  {
    id: 'phases',
    label: 'PHASES',
    arabic: 'مَرَاحِل',
    sublabel: 'Phase Pages',
    href: '/phase/haal-01',
    color: '#2d8a87',
    x: 240,
    y: 240,
    desc: "The Five Haals — stages of the caretaker's journey",
    terms: ['Haal 01', 'Haal 02', 'Haal 03', 'Haal 04', 'Haal 05'],
  },
  {
    id: 'irl',
    label: 'IRL LENS',
    arabic: 'عَدَسَة',
    sublabel: 'Inner Reality Lens',
    href: '/inner-reality-lens',
    color: '#D4A82A',
    x: 560,
    y: 160,
    desc: 'The application tool — recognition arc from Mata to Uboor',
    terms: ['Mata', 'Encounter', 'Examination', 'Recognition', 'Uboor'],
  },
];

const CONNECTIONS = [
  { from: 'keys', to: 'ayat' },
  { from: 'keys', to: 'phases' },
  { from: 'ayat', to: 'names' },
  { from: 'phases', to: 'names' },
  { from: 'names', to: 'irl' },
  { from: 'ayat', to: 'irl' },
];

export default function FrameworkFlowDiagram() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const getNode = (id: string) => FLOW_NODES.find(n => n.id === id)!;

  const handleMouseEnter = useCallback((id: string) => setHoveredId(id), []);
  const handleMouseLeave = useCallback(() => setHoveredId(null), []);

  return (
    <section className="py-24 px-6 relative overflow-hidden" style={{ background: '#060d12' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,105,20,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.03) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="pixel-border-gold-top" />

      <div className="max-w-[1280px] mx-auto relative z-10">
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.7)' }}>
            ✦ Framework Architecture
          </p>
          <h2 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
            The Flow of Recognition
          </h2>
          <p className="font-mono text-sm max-w-xl mx-auto" style={{ color: 'rgba(245,240,232,0.4)' }}>
            Keys → Ayat → Names → Phases → IRL Lens — hover any node to explore, click to enter
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-12">

          {/* SVG Diagram — fixed size container prevents layout shifts */}
          <div className="flex-shrink-0" style={{ width: 680, maxWidth: '100%', overflowX: 'auto' }}>
            <svg
              viewBox="0 0 680 340"
              width="680"
              height="340"
              className="max-w-full"
              style={{ minWidth: 320, display: 'block' }}
            >
              {/* Background circles — no pointer events */}
              {[120, 200, 280].map(r => (
                <circle key={r} cx="340" cy="170" r={r} fill="none" stroke="rgba(139,105,20,0.06)" strokeWidth="1" style={{ pointerEvents: 'none' }} />
              ))}

              {/* Connection lines — no pointer events */}
              {CONNECTIONS.map((conn, i) => {
                const from = getNode(conn.from);
                const to = getNode(conn.to);
                const isActive = hoveredId === conn.from || hoveredId === conn.to;
                return (
                  <line
                    key={i}
                    x1={from.x} y1={from.y}
                    x2={to.x} y2={to.y}
                    stroke={isActive ? 'rgba(179,138,42,0.6)' : 'rgba(139,105,20,0.15)'}
                    strokeWidth={isActive ? 1.5 : 0.75}
                    strokeDasharray={isActive ? 'none' : '4 4'}
                    style={{ transition: 'stroke 0.25s ease, stroke-width 0.25s ease', pointerEvents: 'none' }}
                  />
                );
              })}

              {/* Flow dots on connections — no pointer events */}
              {CONNECTIONS.map((conn, i) => {
                const from = getNode(conn.from);
                const to = getNode(conn.to);
                const mx = (from.x + to.x) / 2;
                const my = (from.y + to.y) / 2;
                const isActive = hoveredId === conn.from || hoveredId === conn.to;
                return (
                  <circle
                    key={`dot-${i}`}
                    cx={mx} cy={my} r={2}
                    fill={isActive ? 'rgba(179,138,42,0.8)' : 'rgba(139,105,20,0.3)'}
                    style={{ transition: 'fill 0.25s ease', pointerEvents: 'none' }}
                  />
                );
              })}

              {/* Nodes — hit area is FIRST (bottom layer), all visuals have pointerEvents:none */}
              {FLOW_NODES.map(node => {
                const isHovered = hoveredId === node.id;
                const r = node.id === 'irl' ? 38 : 32;
                return (
                  <g key={node.id}>
                    {/* Invisible hit area — FIRST so it's the base capture layer, covers full hover zone */}
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={r + 12}
                      fill="transparent"
                      stroke="none"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => handleMouseEnter(node.id)}
                      onMouseLeave={handleMouseLeave}
                    />
                    {/* All visual elements below have pointerEvents:none so they never intercept mouse */}
                    {/* Glow ring — always in DOM, opacity transitions smoothly */}
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={r + 8}
                      fill="none"
                      stroke={node.color}
                      strokeWidth="1"
                      opacity={isHovered ? 0.3 : 0}
                      style={{ transition: 'opacity 0.25s ease', pointerEvents: 'none' }}
                    />
                    {/* Main circle */}
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={r}
                      fill={isHovered ? node.color : '#0d1a14'}
                      stroke={node.color}
                      strokeWidth={isHovered ? 2 : 1}
                      style={{ transition: 'fill 0.25s ease, stroke-width 0.25s ease', pointerEvents: 'none' }}
                    />
                    {/* Inner decorative ring */}
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={r - 6}
                      fill="none"
                      stroke={node.color}
                      strokeWidth="0.5"
                      opacity="0.3"
                      style={{ pointerEvents: 'none' }}
                    />
                    {/* Label */}
                    <text
                      x={node.x}
                      y={node.y - 4}
                      textAnchor="middle"
                      fill={isHovered ? '#0d1a14' : node.color}
                      fontSize={node.id === 'irl' ? '8' : '7'}
                      fontFamily="monospace"
                      fontWeight="900"
                      letterSpacing="1"
                      style={{ transition: 'fill 0.25s ease', pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {node.label}
                    </text>
                    {/* Arabic */}
                    <text
                      x={node.x}
                      y={node.y + 9}
                      textAnchor="middle"
                      fill={isHovered ? 'rgba(13,26,20,0.8)' : `${node.color}80`}
                      fontSize="8"
                      fontFamily="serif"
                      style={{ transition: 'fill 0.25s ease', pointerEvents: 'none', userSelect: 'none' }}
                    >
                      {node.arabic}
                    </text>
                  </g>
                );
              })}

              {/* Center label */}
              <text x="340" y="168" textAnchor="middle" fill="rgba(139,105,20,0.2)" fontSize="7" fontFamily="monospace" letterSpacing="2" style={{ pointerEvents: 'none' }}>RECOGNITION</text>
              <text x="340" y="178" textAnchor="middle" fill="rgba(139,105,20,0.15)" fontSize="6" fontFamily="monospace" letterSpacing="1" style={{ pointerEvents: 'none' }}>FRAMEWORK</text>
            </svg>
          </div>

          {/* Node detail panel */}
          <div className="flex-1 w-full" style={{ minHeight: 280 }}>
            {hoveredId ? (
              (() => {
                const node = FLOW_NODES.find(n => n.id === hoveredId)!;
                return (
                  <div className="p-6 rounded-xl" style={{ background: `${node.color}08`, border: `1px solid ${node.color}25`, borderLeft: `3px solid ${node.color}` }}>
                    <div className="flex items-center gap-3 mb-3">
                      <span style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: node.color, direction: 'rtl' }}>{node.arabic}</span>
                      <div>
                        <h3 className="font-mono text-lg font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>{node.label}</h3>
                        <p className="font-mono text-[10px]" style={{ color: 'rgba(245,240,232,0.4)' }}>{node.sublabel}</p>
                      </div>
                    </div>
                    <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>{node.desc}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {node.terms.map((t, i) => (
                        <span key={i} className="font-mono text-[9px] tracking-widest uppercase px-2 py-1" style={{ background: `${node.color}12`, border: `1px solid ${node.color}25`, color: node.color }}>
                          {t}
                        </span>
                      ))}
                    </div>
                    <Link
                      href={node.href}
                      className="inline-block font-mono text-[10px] tracking-widest uppercase px-5 py-2.5 transition-colors"
                      style={{ background: `${node.color}CC`, color: '#0d1a14', border: `1px solid ${node.color}` }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = node.color; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = `${node.color}CC`; }}
                    >
                      Enter {node.label} →
                    </Link>
                  </div>
                );
              })()
            ) : (
              <div className="p-6 rounded-xl" style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(44,74,62,0.3)' }}>
                <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.5)' }}>Framework Nodes</p>
                <div className="flex flex-col gap-3">
                  {FLOW_NODES.map(node => (
                    <Link
                      key={node.id}
                      href={node.href}
                      className="flex items-center gap-3 p-3 transition-all duration-200"
                      style={{ border: `1px solid ${node.color}20`, background: `${node.color}06` }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = `${node.color}50`; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = `${node.color}20`; }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ background: `${node.color}15`, border: `1px solid ${node.color}30` }}>
                        <span style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: node.color, direction: 'rtl' }}>{node.arabic.slice(0, 2)}</span>
                      </div>
                      <div>
                        <span className="font-mono text-[11px] font-black uppercase" style={{ color: '#F5F0E8' }}>{node.label}</span>
                        <span className="font-mono text-[9px] ml-2" style={{ color: 'rgba(245,240,232,0.3)' }}>{node.sublabel}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Glossary CTA */}
            <div className="mt-4 p-4 rounded-lg" style={{ background: 'rgba(139,105,20,0.06)', border: '1px solid rgba(139,105,20,0.15)' }}>
              <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: 'rgba(139,105,20,0.6)' }}>Sacred Language Glossary</p>
              <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, marginBottom: 8 }}>
                Taqwah, Ihsan, Wujud, Furqan, Amanah, Falah, Yaqeen — every term defined with etymology and Quranic roots.
              </p>
              <Link href="/sl-glossary" className="font-mono text-[10px] tracking-widest uppercase transition-colors border-b"
                style={{ color: 'rgba(179,138,42,0.7)', borderColor: 'rgba(179,138,42,0.3)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#D4A82A'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(179,138,42,0.7)'; }}>
                Open SL Glossary →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
